import { useEffect, useState } from "react"
import { useTheme } from "../../contexts/ThemeContext"
import useTranslation from "../../hooks/useTranslation"

interface SplashScreenProps {
  onComplete: () => void
  isLoading?: boolean
}

export default function SplashScreen({ onComplete, isLoading = true }: SplashScreenProps) {
  const { primaryColor } = useTheme()
  const { t } = useTranslation()
  const [showContent, setShowContent] = useState(false)
  const [progress, setProgress] = useState(0)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Show content after a brief delay for smooth animation
    const contentTimer = setTimeout(() => {
      setShowContent(true)
    }, 300)

    // Animate progress bar
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval)
          return 100
        }
        return prev + 2
      })
    }, 50)

    // Auto complete after minimum time if not loading
    const minDisplayTime = setTimeout(() => {
      if (!isLoading) {
        onComplete()
      }
    }, 3000)

    return () => {
      clearTimeout(contentTimer)
      clearTimeout(minDisplayTime)
      clearInterval(progressInterval)
    }
  }, [onComplete, isLoading])

  // Complete when loading is done and minimum time has passed
  useEffect(() => {
    if (!isLoading && progress >= 100 && !isExiting) {
      const exitTimer = setTimeout(() => {
        setIsExiting(true)
      }, 800)
      
      const completeTimer = setTimeout(() => {
        onComplete()
      }, 1300) // Extra time for exit animation
      
      return () => {
        clearTimeout(exitTimer)
        clearTimeout(completeTimer)
      }
    }
  }, [isLoading, progress, onComplete, isExiting])

  return (
    <div className={`fixed inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100 flex flex-col items-center justify-center z-50 overflow-hidden transition-all duration-500 ${
      isExiting ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
    }`}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, ${primaryColor} 2px, transparent 2px), radial-gradient(circle at 75% 75%, ${primaryColor} 2px, transparent 2px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-4 h-4 rounded-full opacity-20 animate-bounce" 
           style={{ backgroundColor: primaryColor, animationDelay: '0s', animationDuration: '3s' }}></div>
      <div className="absolute top-32 right-16 w-3 h-3 rounded-full opacity-20 animate-bounce" 
           style={{ backgroundColor: primaryColor, animationDelay: '1s', animationDuration: '3s' }}></div>
      <div className="absolute bottom-32 left-20 w-5 h-5 rounded-full opacity-20 animate-bounce" 
           style={{ backgroundColor: primaryColor, animationDelay: '2s', animationDuration: '3s' }}></div>

      {/* Main Content */}
      <div className={`flex flex-col items-center transition-all duration-1000 ${
        showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        
        {/* Logo Container */}
        <div className="relative mb-12">
          {/* Outer Glow Ring */}
          <div className="absolute inset-0 rounded-full animate-pulse" 
               style={{ 
                 boxShadow: `0 0 40px ${primaryColor}30`,
                 animationDuration: '2s'
               }}>
          </div>
          
          {/* Animated Ring */}
          <div className="absolute inset-0 rounded-full border-4 border-transparent animate-spin" 
               style={{ 
                 borderTopColor: primaryColor,
                 borderRightColor: `${primaryColor}40`,
                 animationDuration: '3s'
               }}>
          </div>
          
          {/* Logo */}
          <div className="relative bg-white rounded-full p-8 shadow-2xl border-4 border-gray-100 transform hover:scale-105 transition-transform duration-300">
            <img 
              src="/logo.svg" 
              alt="" 
              className="w-28 h-28 sm:w-36 sm:h-36 object-contain"
            />
          </div>
        </div>

        {/* Loading Section */}
        <div className="w-full max-w-sm px-4">
          {/* Progress Bar */}
          <div className="relative mb-6">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
              <div 
                className="h-full rounded-full transition-all duration-500 ease-out relative"
                style={{ 
                  width: `${progress}%`,
                  background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}dd)`,
                  boxShadow: `0 0 15px ${primaryColor}60`
                }}
              >
                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
              </div>
            </div>
            {/* Progress percentage */}
            <div className="text-center mt-2">
              <span className="text-sm font-medium text-gray-600">{Math.round(progress)}%</span>
            </div>
          </div>

          {/* Loading Text */}
          <div className="text-center">
            <p className="text-lg font-medium text-gray-700 mb-3">
              {isLoading ? t('common.loading') : t('common.welcome')}
            </p>
            
            {/* Loading Dots Animation */}
            {isLoading && (
              <div className="flex justify-center space-x-2 rtl:space-x-reverse">
                <div className="w-3 h-3 rounded-full animate-bounce" 
                     style={{ backgroundColor: primaryColor, animationDelay: '0ms' }}></div>
                <div className="w-3 h-3 rounded-full animate-bounce" 
                     style={{ backgroundColor: primaryColor, animationDelay: '150ms' }}></div>
                <div className="w-3 h-3 rounded-full animate-bounce" 
                     style={{ backgroundColor: primaryColor, animationDelay: '300ms' }}></div>
              </div>
            )}
            
            {/* Success message when loading is complete */}
            {!isLoading && progress >= 100 && (
              <div className="flex justify-center items-center text-green-600">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">{t('common.success')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Decoration */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="text-center">
          <div className="flex justify-center space-x-2 rtl:space-x-reverse mb-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 rounded-full opacity-40 animate-pulse"
                style={{ 
                  backgroundColor: primaryColor,
                  animationDelay: `${i * 300}ms`,
                  animationDuration: '2s'
                }}
              ></div>
            ))}
          </div>
          <p className="text-xs text-gray-400 font-medium">
            Powered by Besalla
          </p>
        </div>
      </div>


    </div>
  )
}